import { useCallback, useLayoutEffect, useRef, type RefObject } from "react";

/**
 * A caixa de texto que cresce com o conteúdo (TUT-249 fase 2).
 *
 * O primeiro item do kokonutui a entrar no produto, e o escolhido de
 * propósito: a direção o classifica como **nível 1 — entra sem conversão** e o
 * chama de "primeiro passo recomendado", porque toda observação, motivo de
 * cancelamento e nota longa do produto hoje mora numa caixa de tamanho fixo,
 * brigando com a barra de rolagem. Ver `docs/design/kokonutui-direcao.md`.
 *
 * ## O que é da casa, e não da biblioteca
 *
 * A biblioteca dá a coreografia; a casa dá o relógio e a acessibilidade. Aqui
 * isso significa três coisas:
 *
 * 1. **Nada de `useEffect`.** A medição corre em `useLayoutEffect`, antes da
 *    pintura: com `useEffect` a caixa nasce com uma altura e salta para outra,
 *    e o salto é visível justamente para quem digita rápido.
 * 2. **Zerar antes de medir, e o que a medição DESMENTIU.** A receita canônica
 *    manda pôr a altura em `auto` antes de ler `scrollHeight`, porque ele não
 *    diminuiria sozinho — sem isso a caixa cresceria e nunca mais encolheria.
 *    **Medido no Chromium que a e2e fixa: não é verdade aqui.** Removendo a
 *    linha, a caixa continuou encolhendo, e a prova de encolhimento passou
 *    igual. A linha FICA — ela é barata e é o comportamento garantido pela
 *    especificação, que é o que vale para o próximo motor —, mas fica sabido
 *    que **nenhuma guarda cobre a remoção dela**: quem a apagar não vai ser
 *    avisado por teste nenhum.
 * 3. **Teto opcional, e a rolagem volta quando ele chega.** Caixa que cresce
 *    sem limite empurra os botões do diálogo para fora da tela — e aí a pessoa
 *    não consegue salvar o que acabou de escrever.
 *
 * ## `rows` continua mandando no tamanho INICIAL
 *
 * O hook só reage ao conteúdo. Quem decide com quantas linhas a caixa nasce é
 * o atributo `rows` de sempre, então nada muda para quem não liga a opção.
 */
export function useAutoResizeTextarea(
  ativo: boolean,
  valor: unknown,
  alturaMaximaEmPx?: number,
): {
  ref: RefObject<HTMLTextAreaElement | null>;
  ajustar: () => void;
} {
  const ref = useRef<HTMLTextAreaElement>(null);

  const ajustar = useCallback(() => {
    const elemento = ref.current;
    if (!elemento || !ativo) return;
    // Zera antes de medir — ver o item 2 do cabeçalho.
    elemento.style.height = "auto";
    const desejada = elemento.scrollHeight;
    const passouDoTeto = Boolean(
      alturaMaximaEmPx && desejada > alturaMaximaEmPx,
    );
    elemento.style.height = `${passouDoTeto ? alturaMaximaEmPx : desejada}px`;
    // A rolagem só aparece quando o teto foi alcançado: antes dele ela seria
    // exatamente o atrito que este hook existe para remover.
    elemento.style.overflowY = passouDoTeto ? "auto" : "hidden";
  }, [ativo, alturaMaximaEmPx]);

  // `valor` entra nas dependências para a caixa acompanhar o texto que vem de
  // FORA — abrir o diálogo já preenchido, ou um reset de formulário, muda o
  // conteúdo sem passar por nenhum `onChange`.
  useLayoutEffect(ajustar, [ajustar, valor]);

  return { ref, ajustar };
}
